/**
 * Task attachment hooks - same style as the ticket attachment hooks in
 * useAgentTicketDetail.ts, scoped to /agent/tasks/{id}/attachments.
 *
 * Wraps:
 *   GET    .../attachments                            -> useTaskAttachments
 *   POST   .../attachments (multipart)                -> useUploadTaskAttachment
 *   DELETE .../attachments/{attId}                    -> useDeleteTaskAttachment
 *   GET    .../attachments/{attId}/download-url       -> fetchTaskAttachmentDownloadUrl
 *
 * Uploads happen from the Edit modal after the task exists (plan
 * section 5 - attachments are added after create). Same 10 MB cap as
 * ticket attachments; the caller enforces it client-side as a hint
 * and the server 413 is the backstop.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import { TASKS_QUERY_KEY } from "@/hooks/useTasks";
import type { TaskAttachmentResponse } from "@/types/api";

export function taskAttachmentsQueryKey(
  taskId: string,
): readonly unknown[] {
  return [...TASKS_QUERY_KEY, "attachments", taskId] as const;
}

/** The task's files, upload order, uploader names resolved. */
export function useTaskAttachments(
  taskId: string | null,
): UseQueryResult<TaskAttachmentResponse[]> {
  return useQuery({
    queryKey: taskAttachmentsQueryKey(taskId ?? ""),
    enabled: !!taskId,
    queryFn: async () => {
      const response = await api.get<TaskAttachmentResponse[]>(
        `/agent/tasks/${taskId}/attachments`,
      );
      return response.data;
    },
  });
}

/**
 * Upload one file via multipart/form-data. One POST per file so a
 * rejected file (too large) surfaces its own error without rolling
 * back siblings - same policy as ticket uploads. Invalidates the
 * whole tasks prefix because the row's attachment_count changed.
 */
export function useUploadTaskAttachment(
  taskId: string,
): UseMutationResult<TaskAttachmentResponse, Error, File> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      // Content-Type: undefined makes axios fall back to the multipart
      // boundary it generates from FormData - the default
      // "application/json" would send a broken multipart body.
      const response = await api.post<TaskAttachmentResponse>(
        `/agent/tasks/${taskId}/attachments`,
        form,
        { headers: { "Content-Type": undefined as unknown as string } },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
  });
}

/** Hard-delete one attachment (row + Spaces object). */
export function useDeleteTaskAttachment(
  taskId: string,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      await api.delete(`/agent/tasks/${taskId}/attachments/${attachmentId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
  });
}

/**
 * Signed-URL fetcher, same shape as the agent ticket attachment
 * download: fetch the JSON with the Bearer header, then open the
 * returned Spaces URL - a plain anchor href cannot carry the JWT.
 */
export async function fetchTaskAttachmentDownloadUrl(
  taskId: string,
  attachmentId: string,
): Promise<string> {
  const response = await api.get<{ url: string }>(
    `/agent/tasks/${taskId}/attachments/${attachmentId}/download-url`,
  );
  return response.data.url;
}
